import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberParam, useQueryParams } from 'use-query-params'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import NoticePopover from '@components/NoticePopover'
import ScrollToTopButton from '@components/ScrollToTopButton'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusNotFreeUser } from '@modules/auth/types'
import { DEFAULT_SHORTLIST_PROFILES_PER_PAGE } from '@modules/shortlist/constants'
import { useShortlistPageData } from '@modules/shortlist/hooks/useShortlistMap'
import { useSortProfileValuesMap } from '@modules/shortlist/hooks/useSortProfileValuesMap'
import { ShortlistSortProfileValue } from '@modules/shortlist/types'
import { PaginationItem, PaginationItemProps, Skeleton, TableBody, TableRow } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import { useSavedScrollPosition } from '@shared/hooks/useSavedScrollPosition'

import { PagePaper, PagePaperInner, TableNavigationBox } from '../../index.styles'
import { DeleteAlertsButton } from './components/DeleteAlertsButton'
import ManageTable from './components/ManageTable/ManageTable'
import { Container, HeadContainer, ItemsTotalTypography, StyledTableCell } from './index.styles'
import { ManagePagePropsInterface } from './types'

const getPaginationId = (item: PaginationItemProps, prefix = '') => {
  const idMap: Record<string, string> = {
    page: `manage_alerts-page_${item.page ?? ''}`,
    previous: 'manage_alerts-navigation-left_arrow',
    next: 'manage_alerts-navigation-right_arrow',
  }

  return idMap[item.type!] ?? ''
}

const ManagePage = ({ alertsListId }: ManagePagePropsInterface) => {
  const { t } = useTranslation()
  const [tablePage, setTablePage] = useState(1)
  const [shortlistSort, setShortlistSort] = useState<ShortlistSortProfileValue>(
    ShortlistSortProfileValue.ALERT_DATE_ADDED_DESCENDING,
  )
  const [isConfirmAlertsRemovePopupShown, setIsConfirmAlertsRemovePopupShown] = useState(false)
  const [selectedProfiles, setSelectedProfiles] = useState<number[]>([])
  const [selectProfilesNoticeAnchor, setSelectProfilesNoticeAnchor] =
    useState<Nullable<{ anchor: Nullable<HTMLElement>; text: string }>>(null)

  const [query, setQuery] = useQueryParams({
    page: NumberParam,
  })
  const { user } = useAuthContext()
  const { trackActivity } = useTrackUserActivity()

  const isSubscriber = useMemo(() => isP1PlusNotFreeUser(user?.subscriptionLevel as number), [user])

  const sortProfileValuesMap = useSortProfileValuesMap()

  const { profilesPageData, totalProfilesAmount, isPageDataLoading } = useShortlistPageData({
    shortlistId: alertsListId,
    isEmptyShortlist: false,
    from: Math.max(tablePage - 1, 0) * DEFAULT_SHORTLIST_PROFILES_PER_PAGE,
    sort: sortProfileValuesMap[shortlistSort].sort,
    allowNotSortable: true,
    filters: [],
  })

  const { saveScroll } = useSavedScrollPosition({ dependencies: [isPageDataLoading], condition: !isPageDataLoading })

  useEffect(() => {
    if (!isPageDataLoading && query.page) {
      setTablePage(query.page)
      setQuery({ page: undefined })
    }
  }, [isPageDataLoading, query.page, setQuery])

  const totalPages = useMemo(() => {
    return totalProfilesAmount ? Math.ceil(totalProfilesAmount / DEFAULT_SHORTLIST_PROFILES_PER_PAGE) : 0
  }, [totalProfilesAmount])

  const profiles = useMemo(
    () => profilesPageData.map(profilePageData => ({ id: profilePageData.globalId, '@type': profilePageData.profileType })),
    [profilesPageData],
  )

  const handleSortingChange = useCallback((sort: ShortlistSortProfileValue) => {
    setShortlistSort(sort)
    setTablePage(1)
  }, [])

  const shouldShowPagination = useMemo(() => {
    return true
  }, [])

  const { profilesAlertsMap } = useAlertDataForProfiles(profiles, isSubscriber && !!profiles.length)

  const data = useMemo(() => {
    return profilesPageData.map(p => {
      const alert = profilesAlertsMap[p.globalId]
      return {
        ...p,
        frequency: alert?.alertFrequency,
      }
    })
  }, [profilesPageData, profilesAlertsMap])

  const onPageChange = useCallback((e: React.ChangeEvent<unknown>, newPage: number) => {
    setTablePage(newPage)
    if (window.scrollY > 250) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  const handleDeleteModalOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedProfiles.length) {
      setIsConfirmAlertsRemovePopupShown(true)
      return
    }
    setSelectProfilesNoticeAnchor({
      anchor: e.currentTarget,
      text: translationKeys.YOU_NEED_TO_SELECT,
    })
  }

  const handleClickProfile = () => {
    saveScroll()
    setQuery({ page: tablePage }, 'pushIn')
  }

  return (
    <div>
      <PagePaper>
        <PagePaperInner>
          <Container>
            <HeadContainer>
              <Typography variant="h2">{t(translationKeys.MANAGE_ALERTS)}</Typography>
              <ItemsTotalTypography variant="caption" fontWeight={400}>
                {t(translationKeys.MANAGE_ALERTS_ITEMS_TOTAL, { count: totalProfilesAmount })}
              </ItemsTotalTypography>
            </HeadContainer>
            <div>
              <DeleteAlertsButton onClick={handleDeleteModalOpen} />
            </div>
            {isPageDataLoading ? (
              <TableBody>
                <TableRow>
                  <StyledTableCell>
                    <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            ) : (
              <ManageTable
                pageData={data}
                sort={shortlistSort}
                onSortChange={handleSortingChange}
                isConfirmAlertsRemovePopupShown={isConfirmAlertsRemovePopupShown}
                setIsConfirmAlertsRemovePopupShown={setIsConfirmAlertsRemovePopupShown}
                setSelectedProfiles={setSelectedProfiles}
                selectedProfiles={selectedProfiles}
                alertsListId={alertsListId}
                onClickProfile={handleClickProfile}
              />
            )}
          </Container>
        </PagePaperInner>
      </PagePaper>
      {shouldShowPagination && (
        <TableNavigationBox>
          <Pagination
            count={totalPages}
            page={tablePage}
            onChange={onPageChange}
            renderItem={item => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <PaginationItem {...item} id={getPaginationId(item)} />
            )}
          />
        </TableNavigationBox>
      )}
      <NoticePopover
        id={selectProfilesNoticeAnchor ? 'select_profiles_notice_popover' : undefined}
        anchor={selectProfilesNoticeAnchor?.anchor || null}
        onClose={() => {
          trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.DELETE_MODAL.CLOSE)
          setSelectProfilesNoticeAnchor(null)
        }}
        title={t(translationKeys.NO_ITEM_SELECTED)}
        text={t(selectProfilesNoticeAnchor?.text as string)}
      />
      <ScrollToTopButton />
    </div>
  )
}

export default ManagePage
